const AI_DESIGNER_API_URL =
  import.meta.env.VITE_AI_DESIGNER_API_URL || 'https://design.optisigns.com/api';
const AI_DESIGNER_SECRET = import.meta.env.VITE_AI_DESIGNER_SECRET || '';

export interface DesignData {
  id?: string;
  canvas: {
    version: string;
    objects: unknown[];
    background?: string;
  };
  canvasWidth: number;
  canvasHeight: number;
  editor?: {
    fonts?: FontDefinition[];
  };
  designName: string;
  metadata: {
    userPrompt: string;
    orientation: string;
    colorPalette?: {
      color1: string;
      color2: string;
      color3: string;
      color4: string;
    };
    templateUsed?: string;
    generatedAt: string;
  };
}

export interface FontDefinition {
  family: string;
  type: string;
  category?: string;
  isBrandFont?: boolean;
  url?: string | string[];
}

/** Generate time-based HMAC-SHA256 auth key (same as Canvas 2.0). */
async function generateTimeBasedKey(secret: string): Promise<string> {
  const timestamp = Date.now();
  const encoder = new TextEncoder();
  const key = await crypto.subtle.importKey(
    'raw',
    encoder.encode(secret),
    { name: 'HMAC', hash: 'SHA-256' },
    false,
    ['sign'],
  );
  const signature = await crypto.subtle.sign('HMAC', key, encoder.encode(timestamp.toString()));
  const hashHex = Array.from(new Uint8Array(signature))
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('');
  return `${timestamp}-${hashHex}`;
}

const STATIC_KEY = import.meta.env.VITE_AI_DESIGNER_STATIC_KEY || '';

/**
 * Decrypt the XOR double-encrypted, base64-encoded secret (same as Canvas 2.0).
 * Flow: base64 decode → XOR with reversed static key → XOR with original static key.
 */
function decryptSecret(): string {
  const decoded = atob(AI_DESIGNER_SECRET);
  const reversedKey = STATIC_KEY.split('').reverse().join('');

  // Pass 1: XOR with reversed key
  let pass1 = '';
  for (let i = 0; i < decoded.length; i++) {
    pass1 += String.fromCharCode(
      decoded.charCodeAt(i) ^ reversedKey.charCodeAt(i % reversedKey.length),
    );
  }

  // Pass 2: XOR with original key
  let result = '';
  for (let i = 0; i < pass1.length; i++) {
    result += String.fromCharCode(
      pass1.charCodeAt(i) ^ STATIC_KEY.charCodeAt(i % STATIC_KEY.length),
    );
  }

  return result;
}

/** Fetch full design data (canvas JSON + fonts + metadata) by designId. */
export async function fetchDesignById(designId: string): Promise<DesignData> {
  const secret = decryptSecret();
  const apiKey = await generateTimeBasedKey(secret);

  const response = await fetch(`${AI_DESIGNER_API_URL}/optisigns-designs/${designId}`, {
    headers: {
      Authorization: `Bearer ${apiKey}`,
      'Content-Type': 'application/json',
    },
  });

  if (!response.ok) {
    throw new Error(`Failed to fetch design ${designId}: ${response.status}`);
  }

  const data = (await response.json()) as DesignData;
  data.id = designId;
  return data;
}
